import React, {useContext} from 'react';
import {Stack, Typography, Button, Box, LinearProgress, Tooltip} from '@mui/material';
import useMediaQuery from '@mui/material/useMediaQuery';
import HealthCategoryNav from '../../../../components/NavigationBar/HealthCategoryNavigationBar';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import DynamicProgressBar from '../../../../components/progressBars/dynamicProgressBar';
import getHealthFunctions from '../../../Results/helperfunctions';
import {CLIENT_TYPES} from '../../../../api/clientconfig';
import {getClientConfig, getClientTypeFromOrgId, ORGANIZATION_IDS} from '../../../../config/clientThemes';
import ShareButtons from '../../../../components/buttons/ShareButtons';
import {identifyClientType} from '../../../../utils/identifyClientType';
import mainLogo from '../../../../images/black_letters_with_blue_shadow.svg';
import APIObjectsContext from '../../../../context/APIObjectsContext';
import {useNavigate} from 'react-router-dom';

export const AllHealthScore = ({
  value,
  title,
  symptoms = [],
  onBackBtnClick,
  activeCategoryIndex,
  handleCategorySelect,
  getCategories,
  isShowAllResultsCard,
  findProductBtnHandlerForSymptomPage,
  config = {},
  organization = {},
}) => {
  // Ensure getCategories is a function and returns an array
  const categories = typeof getCategories === 'function' ? getCategories() : [];
  const isSmallScreen = useMediaQuery('(max-width:499px)');
  const isGalaxyFold = useMediaQuery('(max-width:280px)');
  const isLargeScreen = useMediaQuery(theme => theme.breakpoints.up('lg'));

  // Get client type and config
  const [clientType, setClientType] = React.useState(() => {
    const orgId = parseInt(localStorage.getItem('organization_id'));
    if (!isNaN(orgId)) {
      return getClientTypeFromOrgId(orgId);
    }
    localStorage.setItem('organization_id', ORGANIZATION_IDS.DEFAULT.toString());
    return CLIENT_TYPES.DEFAULT;
  });

  React.useEffect(() => {
    setClientType(identifyClientType());
  }, []);

  const clientConfig = getClientConfig(clientType);

  // Health score calculations
  let allHealthVal = Number(value);
  let integerallHealthVal = Math.ceil(allHealthVal);

  const healthFunctions = getHealthFunctions('overall health score');
  const scaledValue = healthFunctions.getValue(integerallHealthVal);
  const statusColor = healthFunctions.getColor(integerallHealthVal);
  const statusText = healthFunctions.getStatus(integerallHealthVal);
  const paramValue = healthFunctions.getScaledValue(integerallHealthVal);

  // Check flags
  const isFromDoktor = window.localStorage.getItem('from_doktor') === 'true';
  const isAtplFlow = window.localStorage.getItem('from_atp') === 'true';

  if (isLargeScreen) {
    return (
      <LargeScreenLayout
        value={value}
        title={title}
        symptoms={symptoms}
        config={clientConfig}
        isShowAllResultsCard={isShowAllResultsCard}
        findProductBtnHandlerForSymptomPage={findProductBtnHandlerForSymptomPage}
        clientType={clientType}
        isSmallScreen={isSmallScreen}
        isGalaxyFold={isGalaxyFold}
        integerallHealthVal={integerallHealthVal}
        statusColor={statusColor}
        statusText={statusText}
        paramValue={paramValue}
        isFromDoktor={isFromDoktor}
        mainLogo={mainLogo}
      />
    );
  }

  return (
    <SmallScreenLayout
      value={value}
      title={title}
      symptoms={symptoms}
      onBackBtnClick={onBackBtnClick}
      activeCategoryIndex={activeCategoryIndex}
      handleCategorySelect={handleCategorySelect}
      getCategories={getCategories}
      config={clientConfig}
      isSmallScreen={isSmallScreen}
      isGalaxyFold={isGalaxyFold}
      isShowAllResultsCard={isShowAllResultsCard}
      findProductBtnHandlerForSymptomPage={findProductBtnHandlerForSymptomPage}
      organization={organization}
      clientType={clientType}
      integerallHealthVal={integerallHealthVal}
      statusColor={statusColor}
      statusText={statusText}
      paramValue={paramValue}
      isFromDoktor={isFromDoktor}
      isAtplFlow={isAtplFlow}
      mainLogo={mainLogo}
      categories={categories}
    />
  );
};

const LargeScreenLayout = ({symptoms, config, clientType, isSmallScreen, isGalaxyFold, mainLogo}) => {
  const cardRef = React.useRef(null);
  const [ShadowClass, setShadowClass] = React.useState(true);

  return (
    <Stack
      display="flex"
      flexDirection="row"
      spacing={1}
      alignItems="center"
      justifyContent="center"
      sx={{width: 1, minHeight: '380px'}}
      className="results-page-wrapper"
    >
      <Stack className="result-page-card large-card" padding={3}>
        <Stack ref={cardRef} className="large-result-component">
          <Stack
            className={ShadowClass ? 'result-component card-shadow' : 'result-component'}
            sx={{
              backgroundColor: clientType === CLIENT_TYPES.DEFAULT ? '#F7F7F7 !important' : undefined,
              minHeight: '380px !important',
            }}
          >
            <Stack padding={2}>
              <Typography
                color="black"
                variant="h4"
                component="h4"
                fontWeight="700"
                sx={{
                  fontSize: isGalaxyFold ? '18px' : isSmallScreen ? '18px' : '18px',
                }}
              >
                Health Scan Result
              </Typography>
              <Typography
                color="black"
                variant="p"
                component="p"
                fontWeight="500"
                sx={{
                  mt: '5px',
                  fontSize: isGalaxyFold ? '12px' : isSmallScreen ? '13px' : '13px',
                  lineHeight: '17px',
                }}
              >
                According to our symptom checker tool, your symptoms most closely match the following conditions.
              </Typography>
            </Stack>

            {symptoms.map((symptom, index) => (
              <Stack paddingLeft={2} paddingRight={2} marginBottom={2} className="progress-item" key={index}>
                <Stack className="prograss-bar">
                  <Tooltip title={symptom.name} placement="top" arrow>
                    <Typography
                      color="black"
                      lineHeight="30px"
                      variant="h4"
                      component="h4"
                      fontWeight="600"
                      whiteSpace="nowrap"
                      overflow="hidden"
                      textOverflow="ellipsis"
                      sx={{
                        fontSize: isGalaxyFold ? '17px' : isSmallScreen ? '17px' : '17px',
                      }}
                    >
                      {symptom.name}
                    </Typography>
                  </Tooltip>
                  <LinearProgress
                    variant="determinate"
                    value={Number(Math.floor(symptom.probability))}
                    sx={{
                      height: isSmallScreen ? '6px' : '7px',
                      borderRadius: 4,
                      '& .MuiLinearProgress-barColorPrimary': {
                        background: config?.symptomChecker?.symptomCheckerBarColor
                          ? config?.symptomChecker.symptomCheckerBarColor
                          : 'linear-gradient(to right, #31A6EE 0%, #35C6C9 50.5%, #A6E177 100%) !important',
                      },
                    }}
                  />
                  <Typography
                    color={
                      config?.symptomChecker?.symptomMatchTextColor
                        ? config?.symptomChecker.symptomMatchTextColor
                        : 'black'
                    }
                    lineHeight="20px"
                    variant="h4"
                    component="h4"
                    fontWeight="700"
                    sx={{
                      fontSize: isGalaxyFold ? '12px' : isSmallScreen ? '12px' : '12px',
                    }}
                  >
                    {
                      {
                        0: 'Closest match',
                        1: 'Somewhat of a match',
                        2: 'Lowest match',
                      }[index]
                    }
                  </Typography>
                </Stack>
                <Stack>
                  <Typography
                    color="black"
                    variant="h4"
                    component="h4"
                    fontWeight="700"
                    sx={{
                      fontSize: isGalaxyFold ? '18px' : isSmallScreen ? '18px' : '18px',
                    }}
                  >
                    {Number(Math.floor(symptom.probability))}%
                  </Typography>
                </Stack>
              </Stack>
            ))}

            <Box className="result-logo" sx={{marginBottom: '10px'}}>
              {clientType === CLIENT_TYPES.ATP ? (
                <a href="https://atplab.com/?rfsn=8347543.b2a03c" target="_blank" rel="noopener noreferrer">
                  <img src={config.logos?.main} height={config.logos?.height || 40} alt="Logo" />
                </a>
              ) : (
                <img src={config?.logos?.main} height={config?.logos?.height} className="" />
              )}
              {clientType !== CLIENT_TYPES.DEFAULT && (
                <Typography sx={{fontSize: '14px'}}>
                  Your health scan was powered by
                  <img src={mainLogo} className="custom-main-logo-for-md" />
                </Typography>
              )}
            </Box>
          </Stack>
        </Stack>
      </Stack>
    </Stack>
  );
};

const SmallScreenLayout = ({
  symptoms,
  onBackBtnClick,
  activeCategoryIndex,
  handleCategorySelect,
  config,
  isSmallScreen,
  isGalaxyFold,
  clientType,
  integerallHealthVal,
  statusColor,
  statusText,
  paramValue,
  isFromDoktor,
  isAtplFlow,
  mainLogo,
  categories,
}) => {
  const cardRef = React.useRef(null);
  const [ShadowClass, setShadowClass] = React.useState(true);
  const navigate = useNavigate();
  const {userAssessmentID, setUserAssessmentID} = useContext(APIObjectsContext);

  return (
    <Stack
      display="flex"
      flexDirection="row"
      spacing={2}
      alignItems="center"
      justifyContent="center"
      sx={{width: 1}}
      className="results-page-wrapper"
      paddingBottom={1}
    >
      <Stack className="result-page-card" backgroundColor="white" padding={3} paddingTop="1px">
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            width: '100%',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: isSmallScreen ? '2px 0px' : '4px 0px',
          }}
        >
          <Stack display="flex" flexDirection="row" style={{cursor: 'pointer'}} onClick={onBackBtnClick}>
            {clientType !== 'DOKTOR_CONNECT' && config?.backButton}
          </Stack>

          {clientType == CLIENT_TYPES.DEFAULT && (
            <Stack sx={{display: 'flex', flexDirection: 'row', alignItems: 'center'}}>
              <Typography
                sx={{
                  cursor: 'pointer',
                  color: '#72D8DA',
                  variant: 'p',
                  component: 'p',
                  fontWeight: '600',
                  fontSize: '14px',
                  textAlign: 'end',
                }}
                onClick={() => {
                  navigate && navigate('/thank_you');
                }}
              >
                Next
              </Typography>
              <ArrowForwardIcon
                sx={{fontSize: '16px', padding: '0px 2px 0px 4px', cursor: 'pointer', color: '#72D8DA'}}
              />
            </Stack>
          )}
        </Box>

        <Stack className="screenshortCard">
          <Stack
            className="top-header"
            display="flex"
            alignItems="center"
            flexDirection="row"
            justifyContent="space-between"
          >
            <Stack className="top-heading">
              <Typography
                color="black"
                variant="h4"
                component="h4"
                fontWeight="bold"
                sx={{
                  fontSize: isGalaxyFold ? '16px' : isSmallScreen ? '18px' : '23px',
                }}
              >
                Overall Health Score
              </Typography>
              <Typography
                color={statusColor}
                variant="p"
                component="p"
                fontWeight="600"
                sx={{
                  fontSize: isGalaxyFold ? '12px' : isSmallScreen ? '14px' : '15px',
                }}
              >
                {statusText}
              </Typography>
            </Stack>
            <Box
              sx={{
                position: 'relative',
                width: '80px',
                height: isSmallScreen ? '64px' : '80px',
                background: '#fff',
              }}
            >
              <Box
                position="relative"
                display="flex"
                justifyContent="center"
                alignItems="center"
                width="100%"
                height="100%"
              >
                <DynamicProgressBar
                  paramValue={paramValue}
                  barColor={statusColor}
                  paramSize={isGalaxyFold ? 60 : isSmallScreen ? 50 : 70}
                  paramThickness={isGalaxyFold ? 6 : isSmallScreen ? 5 : 8}
                />
                <Typography
                  variant="h6"
                  style={{position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)'}}
                  sx={{fontWeight: 'bold'}}
                >
                  {integerallHealthVal}
                </Typography>
              </Box>
            </Box>
          </Stack>

          <Stack
            sx={{
              backgroundColor: clientType === CLIENT_TYPES.DEFAULT ? '#F7F7F7 !important' : undefined,
              position: 'relative',
              minHeight: isSmallScreen && '304px !important',
              bottom: config?.resultPageSlider?.isResultPageSliderVisible ? '30px' : '0px',
            }}
            className={ShadowClass ? 'result-component card-shadow' : 'result-component'}
            ref={cardRef}
          >
            <Stack paddingX={isSmallScreen ? 2 : 3} paddingY={isSmallScreen ? 1 : 3}>
              <Typography
                color="black"
                variant="h4"
                component="h4"
                fontWeight="700"
                sx={{
                  fontSize: isGalaxyFold ? '14px' : isSmallScreen ? '16px' : '22px',
                }}
              >
                Health Scan Result
              </Typography>
              <Typography
                color="black"
                variant="p"
                component="p"
                fontWeight="500"
                sx={{
                  mt: isSmallScreen ? '2px' : 1,
                  fontSize: isGalaxyFold ? '10px' : isSmallScreen ? '10px' : '13px',
                  lineHeight: '17px',
                }}
              >
                According to our symptom checker tool, your symptoms most closely match the following conditions.
              </Typography>
            </Stack>

            {symptoms.map((symptom, index) => (
              <Stack
                paddingLeft={isSmallScreen ? 2 : 3}
                paddingRight={isSmallScreen ? 2 : 3}
                marginBottom={isSmallScreen ? '2px' : 2}
                className="progress-item"
                key={index}
              >
                <Stack className="prograss-bar">
                  <Typography
                    color="black"
                    lineHeight="30px"
                    variant="h4"
                    component="h4"
                    fontWeight="600"
                    sx={{
                      fontSize: isGalaxyFold ? '16px' : isSmallScreen ? '13px' : '17px',
                    }}
                  >
                    {symptom.name}
                  </Typography>
                  <LinearProgress
                    variant="determinate"
                    value={Number(Math.floor(symptom.probability))}
                    sx={{
                      height: isSmallScreen ? '6px' : '6px',
                      borderRadius: 4,
                      '& .MuiLinearProgress-barColorPrimary': {
                        background: config?.symptomChecker?.symptomCheckerBarColor
                          ? config?.symptomChecker.symptomCheckerBarColor
                          : 'linear-gradient(to right, #31A6EE 0%, #35C6C9 50.5%, #A6E177 100%) !important',
                      },
                    }}
                  />
                  <Typography
                    color={
                      config?.symptomChecker?.symptomMatchTextColor
                        ? config?.symptomChecker.symptomMatchTextColor
                        : '#3DA9F8'
                    }
                    lineHeight="20px"
                    variant="h4"
                    component="h4"
                    fontWeight="700"
                    sx={{
                      fontSize: isGalaxyFold ? '10px' : isSmallScreen ? '10px' : '12px',
                    }}
                  >
                    {
                      {
                        0: 'Closest match',
                        1: 'Somewhat of a match',
                        2: 'Lowest match',
                      }[index]
                    }
                  </Typography>
                </Stack>
                <Stack>
                  <Typography
                    color="black"
                    variant="h4"
                    component="h4"
                    fontWeight="700"
                    sx={{
                      fontSize: isGalaxyFold ? '18px' : isSmallScreen ? '18px' : '23px',
                    }}
                  >
                    {Number(Math.floor(symptom.probability))}%
                  </Typography>
                </Stack>
              </Stack>
            ))}

            <Box
              className="result-logo"
              sx={{marginBottom: clientType !== CLIENT_TYPES.DEFAULT && isSmallScreen ? '1px' : '18px'}}
            >
              {clientType === CLIENT_TYPES.ATP ? (
                <a href="https://atplab.com/?rfsn=8347543.b2a03c" target="_blank" rel="noopener noreferrer">
                  <img src={config.logos?.main} height={config.logos?.height || 40} alt="Logo" />
                </a>
              ) : (
                <img src={config?.logos?.main} height={config?.logos?.height} className="" />
              )}
              {clientType !== CLIENT_TYPES.DEFAULT && (
                <Typography
                  sx={{
                    fontSize: isGalaxyFold ? '8px' : '10px',
                    display: 'flex',
                    alignItems: 'center',
                    width: '100%',
                    paddingTop: '8px',
                    justifyContent: 'center',
                  }}
                >
                  Your health scan was powered by
                  <img src={mainLogo} className="custom-main-logo" />
                </Typography>
              )}
            </Box>
          </Stack>

          {config?.resultPageSlider?.isResultPageSliderVisible && categories.length > 0 && (
            <Box sx={{position: 'relative', bottom: '30px'}}>
              <HealthCategoryNav
                categories={categories}
                activeIndex={activeCategoryIndex}
                onSelectCategory={handleCategorySelect}
                isLargeScreen={false}
                clientBaseNavCss={{
                  arrowColor: config?.resultPageSlider?.arrowColor,
                  arrowHOverColor: config?.resultPageSlider?.arrowHOverColor,
                  movingIndicatorColor: config?.resultPageSlider?.movingIndicatorColor,
                }}
              />
            </Box>
          )}

          <Stack className="bottom-info">
            <Typography
              color="black"
              marginTop="16px"
              variant="p"
              component="p"
              fontWeight="400"
              sx={{
                fontSize: isGalaxyFold ? '10px' : isSmallScreen ? '10px' : '12px',
                position: isAtplFlow || config?.resultPageSlider?.isResultPageSliderVisible ? 'relative' : null,
                bottom: isAtplFlow || config?.resultPageSlider?.isResultPageSliderVisible ? '40px' : '0px',
              }}
            >
              These numbers are only estimates and are not a substitute for the judgment of a healthcare professional.
              They are intended to improve awareness and general wellness. Please see our
              <a href="/" className="custom-link-tag">
                How it Works
              </a>
              page.
            </Typography>
          </Stack>
        </Stack>

        <Box
          marginTop="20px"
          sx={{
            position: config?.resultPageSlider?.isResultPageSliderVisible ? 'relative' : null,
            bottom: config?.resultPageSlider?.isResultPageSliderVisible ? '50px' : '0px',
          }}
        >
          {/* { ( clientType === "ATP" || clientType === "HELLO_HEALTH" ) &&
            // <Button
            //   onClick={ findProductBtnHandlerForSymptomPage }
            //   fullWidth
            //   className={ 'btn-shadow' }
            //   sx={ {
            //     height: isSmallScreen ? '50px' : '60px',
            //     borderRadius: isFromDoktor || isAtplFlow || ( clientType === "HELLO_HEALTH" ) ? '10px' : '40px',
            //     marginBottom: isSmallScreen ? '6px' : '10px',
            //     background: config?.button?.background,
            //     maxWidth: '400px',
            //     textAlign: 'center',
            //     color: "#fff",
            //     "&:hover": {
            //       color: "black"
            //     },
            //   } }
            // >
            //   <Typography
            //     className="btn-txt"
            //     variant="p"
            //     component="p"
            //     fontWeight="600"
            //     sx={ {
            //       fontSize: isSmallScreen ? '16px' : '20px',
            //     } }
            //   >
            //     👀 Find Products That Can Help
            //   </Typography>
            // </Button>
          } */}

          {isFromDoktor || clientType === 'ATP' || clientType === 'HELLO_HEALTH' ? (
            <Stack
              display="flex"
              className="result-card-footer"
              flexDirection="row"
              justifyContent="center"
              alignItems="center"
              marginTop={isSmallScreen ? '4px' : 1}
              marginBottom={clientType === 'ATP' && '24px'}
            >
              <ShareButtons cardRef={cardRef} socialButtonsCss={config?.socialButtons} />
            </Stack>
          ) : (
            <Stack
              display="flex"
              className="result-card-footer"
              flexDirection="row"
              justifyContent="center"
              alignItems="center"
              marginTop={isSmallScreen ? '4px' : 1}
            >
              <ShareButtons
                cardRef={cardRef}
                item={{userAssessmentID: userAssessmentID}}
                socialButtonsCss={config?.socialButtons}
              />
            </Stack>
          )}
        </Box>
      </Stack>
    </Stack>
  );
};
